import type { FC, PropsWithChildren } from 'react';
import { memo, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { AppContext } from '../../AppContext';

// eslint-disable-next-line import/no-unused-modules
export const pageFixedPortalId = 'mwp-page-fixed-portal';

/**
 * Portal for rendering content above all of the page content in a persistent manner.
 *
 * Use as return React.createPortal(<element>, pageFixedPortalId)
 */
export const PageFixedPortal: FC<{ className?: string }> = memo(props => {
  const { pageFixedPortalRef } = useContext(AppContext);

  return (
    <aside className={props.className} data-testid={pageFixedPortalId} ref={pageFixedPortalRef} />
  );
});

/**
 * Easy insertion point into the page.
 *
 * Use as <IntoPageFixedPortal>{.. your stuff ..}</IntoPageFixedPortal>
 *
 * NOTE: The content only gets inserted on the client. Nothing displays on the server.
 */
export const IntoPageFixedPortal: FC<PropsWithChildren> = ({ children }) => {
  const { pageFixedPortalRef } = useContext(AppContext);

  /* The portal target ref is populated during the commit phase of PageFixedPortal.
   * If this component renders in the same pass (e.g. with cached Apollo data), the
   * ref will still be null on first render. Tracking mount state forces a second
   * render after commit so the portal can actually attach.
   */
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted || !pageFixedPortalRef?.current) return null;

  return createPortal(children, pageFixedPortalRef.current);
};
